/**
 * Flow Screen Definitions
 *
 * Defines the screen codes for each claim flow type.
 * Used to calculate progression percentage based on stepData.
 */

export type FlowType = 'MEDICAL' | 'CANCELLATION' | 'BAGGAGE';

/**
 * Common screens shared by all flows
 */
const COMMON_SCREENS = [
  '00-01-TYPE',           // Request Type
  '00-02-AVIS',           // Legal Notice
  '01-00-DEMANDEUR',      // Contact Preferences
  '01-01-CONTRAT',        // Contract Selection
  '01-02-AUTRES-CONTRATS', // Other Insurance
  '01-03-PAIEMENT-VOYAGE', // Trip Payment Method (conditional — DEV-7479, always counted)
  '02-00-GARANTIE',       // Claim Type Selection
];

/**
 * Medical flow specific screens
 */
const MEDICAL_SCREENS = [
  ...COMMON_SCREENS,
  '02M-01-BENEFICIAIRE',  // Beneficiary
  '02M-02-VOYAGE',        // Travel Confirmation
  '02M-03-SPECIFIC',      // Specific Claim Type (optional - only if multiple types)
  '03M-00-ACCIDENT',      // Specific Verifications
  '03M-01-DETAILS',       // Event Details
  '03M-02-VERIFICATIONS', // Medical Verifications
  '04M-00-REMBOURSEMENT', // Reimbursement Details
  '04-01-FACTURES',       // Invoice Upload
  '04-02-VALIDATION',     // Expense Validation
  '04-03-JUSTIFICATIFS',  // Supporting Documents
  '05-00-ELIGIBILITE',    // Eligibility Analysis
  '05-01-PAIEMENT',       // Banking Info
  '05-02-REVUE',          // Review & Submit
  '05-03-FIN',            // Confirmation
];

/**
 * Cancellation flow specific screens
 */
const CANCELLATION_SCREENS = [
  ...COMMON_SCREENS,
  '02A-01-BENEFICIAIRE',  // Beneficiary (Cancellation)
  '02A-02-VOYAGE',        // Travel Confirmation (Cancellation)
  '03A-01-DETAILS1',      // Cancellation Details 1
  '03A-01-DETAILS2',      // Cancellation Details 2
  '03A-01-DETAILS3',      // Cancellation Details 3
  '03A-01-DETAILS4',      // Cancellation Details 4
  '03A-01-DETAILS5',      // Cancellation Details 5
  '03A-02-VERIFICATIONS', // Cancellation Medical Verification
  '04A-00-CATEGORIE',     // Cancellation Category
  '04-01-FACTURES',       // Invoice Upload
  '04-02-VALIDATION',     // Expense Validation
  '04-03-JUSTIFICATIFS',  // Supporting Documents
  '05-00-ELIGIBILITE',    // Eligibility Analysis
  '05-01-PAIEMENT',       // Banking Info
  '05-02-REVUE',          // Review & Submit
  '05-03-FIN',            // Confirmation
];

/**
 * Baggage flow specific screens
 */
const BAGGAGE_SCREENS = [
  ...COMMON_SCREENS,
  '02B-01-BENEFICIAIRE',  // Beneficiary (Baggage)
  '02B-02-VOYAGE',        // Travel Confirmation (Baggage)
  '03B-01-DETAILS',       // Baggage Details
  '03B-02-VERIFICATIONS', // Baggage Verifications
  '04B-00-REMBOURSEMENT', // Baggage Category
  '04-01-FACTURES',       // Invoice Upload
  '04-02-VALIDATION',     // Expense Validation
  '04-03-JUSTIFICATIFS',  // Supporting Documents
  '05-00-ELIGIBILITE',    // Eligibility Analysis
  '05-01-PAIEMENT',       // Banking Info
  '05-02-REVUE',          // Review & Submit
  '05-03-FIN',            // Confirmation
];

/**
 * Get all screen codes for a specific flow type
 *
 * @param flowType - The type of claim flow
 * @returns Array of screen codes in order
 */
export function getFlowScreens(flowType: FlowType | null): string[] {
  switch (flowType) {
    case 'MEDICAL':
      return MEDICAL_SCREENS;
    case 'CANCELLATION':
      return CANCELLATION_SCREENS;
    case 'BAGGAGE':
      return BAGGAGE_SCREENS;
    default:
      // If flow type unknown, return common screens
      return COMMON_SCREENS;
  }
}

/**
 * Get workflow steps for progress calculation (same logic as ProgressBanner)
 * Only includes sections 01-05 (excludes 00-* screens)
 *
 * DUPLICATED on purpose (historically) with `ProgressBanner.getWorkflowSteps`.
 * Exported so the two implementations can be asserted equal by a test — if they
 * diverge the app reports two different step totals.
 */
export function getWorkflowSteps(flowType: FlowType): string[] {
  const baseSteps = [
    // Section 01: Applicant Information (4 steps)
    '01-00-DEMANDEUR',
    '01-01-CONTRAT',
    '01-02-AUTRES-CONTRATS',
    // Conditional (DEV-7479) but ALWAYS counted in the total — same convention as
    // '05M-02-MANDAT' and the five mutually exclusive '03A-01-DETAILS*' screens.
    '01-03-PAIEMENT-VOYAGE',
    // Section 02: Coverage (1 step)
    '02-00-GARANTIE',
  ];

  if (flowType === 'MEDICAL') {
    return [
      ...baseSteps,
      '02M-01-BENEFICIAIRE',
      '02M-02-VOYAGE',
      // Section 03: Event Details (3 steps)
      '03M-00-ACCIDENT',
      '03M-01-DETAILS',
      '03M-02-VERIFICATIONS',
      // Section 04: Reimbursement (4 steps)
      '04M-00-REMBOURSEMENT',
      '04-01-FACTURES',
      '04-02-VALIDATION',
      '04-03-JUSTIFICATIFS',
      // Section 05: Payment & Submission (3 steps)
      '05-01-PAIEMENT',
      '05M-02-MANDAT',
      '05-03-FIN'
    ]; // Total: 17 steps
  } else if (flowType === 'CANCELLATION') {
    return [
      ...baseSteps,
      '02A-01-BENEFICIAIRE',
      '02A-02-VOYAGE',
      // Section 03: Event Details (6 steps)
      '03A-01-DETAILS1',
      '03A-01-DETAILS2',
      '03A-01-DETAILS3',
      '03A-01-DETAILS4',
      '03A-01-DETAILS5',
      '03A-02-VERIFICATIONS',
      // Section 04: Documents (3 steps)
      '04-01-FACTURES',
      '04-02-VALIDATION',
      '04-03-JUSTIFICATIFS',
      // Section 05: Payment & Submission (2 steps)
      '05-01-PAIEMENT',
      '05-03-FIN'
    ]; // Total: 18 steps
  } else { // BAGGAGE
    return [
      ...baseSteps,
      '02B-01-BENEFICIAIRE',
      '02B-02-VOYAGE',
      // Section 03: Event Details (2 steps)
      '03B-01-DETAILS',
      '03B-02-VERIFICATIONS',
      // Section 04: Reimbursement (4 steps)
      '04B-00-REMBOURSEMENT',
      '04-01-FACTURES',
      '04-02-VALIDATION',
      '04-03-JUSTIFICATIFS',
      // Section 05: Payment & Submission (2 steps)
      '05-01-PAIEMENT',
      '05-03-FIN'
    ]; // Total: 15 steps
  }
}

/**
 * Calculate progress percentage based on current screen position
 * Uses same logic as ProgressBanner for consistency
 *
 * @param currentScreenCode - The current screen code
 * @param flowType - The type of claim flow
 * @returns Progress percentage (0-100)
 */
export function calculateProgressPercentage(
  currentScreenCode: string | null,
  flowType: FlowType | null
): number {
  // If no screen code or in section 00, progress is 0
  if (!currentScreenCode) return 0;

  const sectionCode = currentScreenCode.substring(0, 2);
  if (sectionCode === '00') return 0;

  // If on final screen, progress is 100%
  if (currentScreenCode.includes('FIN')) return 100;

  // If flow type not yet selected, use average estimate
  if (!flowType) {
    // Rough estimate based on common screens completed
    const commonScreens = ['01-00-DEMANDEUR', '01-01-CONTRAT', '01-02-AUTRES-CONTRATS', '01-03-PAIEMENT-VOYAGE', '02-00-GARANTIE'];
    const index = commonScreens.indexOf(currentScreenCode);
    if (index >= 0) {
      // Return rough percentage (these are first 5 screens out of ~17 average)
      return Math.round(((index + 1) / 17) * 100);
    }
    return 0;
  }

  // Get workflow steps for the specific flow type
  const workflowSteps = getWorkflowSteps(flowType);
  const currentStepIndex = workflowSteps.findIndex(step => step === currentScreenCode);

  if (currentStepIndex === -1) return 0;

  const totalSteps = workflowSteps.length;

  // Calculate based on current step position (same as ProgressBanner)
  const stepProgress = ((currentStepIndex + 1) / totalSteps) * 100;

  return Math.min(Math.max(Math.round(stepProgress), 0), 100);
}

/**
 * Get the current step number based on current screen code
 *
 * @param currentScreenCode - The current screen code
 * @param flowType - The type of claim flow
 * @returns Step number (1-based index) or 0 if not found
 */
export function getCurrentStepNumber(
  currentScreenCode: string | null,
  flowType: FlowType | null
): number {
  if (!currentScreenCode) return 0;

  const flowScreens = getFlowScreens(flowType);
  const index = flowScreens.indexOf(currentScreenCode);

  return index >= 0 ? index + 1 : 0;
}

/**
 * Get total number of screens for a flow
 *
 * @param flowType - The type of claim flow
 * @returns Total number of screens
 */
export function getTotalScreens(flowType: FlowType | null): number {
  return getFlowScreens(flowType).length;
}
